import { useEffect, useRef, type RefObject } from "react"

const defaultSeed = 0x9e3779b9
const revealTransitionMs = 220
const mountWarmupFrames = 6

// Pulsing ripple tuning (see `rippleActive`). Ring crests expand outward from
// the ripple origin, fading out past `rippleMaxRadius`.
const rippleFadeMs = 260
const rippleSpeed = 130 // px/second that ring crests travel outward
const ripplePeriod = 130 // px between successive ring crests
const rippleBandWidth = 46 // px thickness of each crest
const rippleMaxRadius = 560 // px beyond which ripples dissipate
const defaultRippleColor = { r: 99, g: 102, b: 241 } // indigo-500
const defaultRippleColorAlt = { r: 165, g: 180, b: 252 } // indigo-300

interface DotGridBackgroundProps {
  className?: string
  gap?: number
  dotSize?: number
  sizeRange?: number
  opacityRange?: number
  revealRadius?: number
  seed?: number
  /** While true, ring crests pulse outward from `rippleOrigin` in `rippleColor`. */
  rippleActive?: boolean
  /** Element whose center the ripples emanate from (defaults to canvas center). */
  rippleOrigin?: RefObject<HTMLElement | null>
  /** RGB color of the ripple crests (defaults to indigo-500). */
  rippleColor?: { r: number; g: number; b: number }
  /** Alternate crest color; successive rings alternate between the two (defaults to indigo-300). */
  rippleColorAlt?: { r: number; g: number; b: number }
}

function mulberry32(seed: number) {
  return function random() {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0

    let value = Math.imul(seed ^ (seed >>> 15), 1 | seed)

    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value

    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

function getDotColor() {
  const isDark =
    typeof document !== "undefined" && document.documentElement.classList.contains("dark")

  return isDark ? { r: 168, g: 162, b: 158 } : { r: 115, g: 115, b: 115 }
}

export function DotGridBackground({
  className,
  gap = 16,
  dotSize = 1,
  sizeRange = 3,
  opacityRange = 0.72,
  revealRadius = 260,
  seed = defaultSeed,
  rippleActive = false,
  rippleOrigin,
  rippleColor = defaultRippleColor,
  rippleColorAlt = defaultRippleColorAlt,
}: DotGridBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const frameRef = useRef<number | null>(null)
  const pointerRef = useRef<{ x: number; y: number } | null>(null)
  const pointerInsideRef = useRef(false)
  const focusWithinRef = useRef(false)
  const activeTargetRef = useRef(0)
  const activeValueRef = useRef(0)
  const lastFrameTimeRef = useRef<number | null>(null)
  const colorRef = useRef(getDotColor())
  const requestDrawRef = useRef<(() => void) | null>(null)
  const warmupFramesRef = useRef(0)
  const rippleTargetRef = useRef(0)
  const rippleValueRef = useRef(0)
  const rippleTravelRef = useRef(0)
  // Track the latest ripple origin/color without re-running the draw effect.
  const rippleOriginRef = useRef(rippleOrigin)
  useEffect(() => {
    rippleOriginRef.current = rippleOrigin
  }, [rippleOrigin])
  const rippleColorRef = useRef(rippleColor)
  useEffect(() => {
    rippleColorRef.current = rippleColor
  }, [rippleColor])
  const rippleColorAltRef = useRef(rippleColorAlt)
  useEffect(() => {
    rippleColorAltRef.current = rippleColorAlt
  }, [rippleColorAlt])

  useEffect(() => {
    const canvas = canvasRef.current
    const parent = canvas?.parentElement

    if (!canvas || !parent) {
      return
    }

    const canvasElement = canvas
    const parentElement = parent
    warmupFramesRef.current = mountWarmupFrames

    function requestDraw() {
      if (frameRef.current !== null) {
        return
      }

      frameRef.current = window.requestAnimationFrame(draw)
    }

    requestDrawRef.current = requestDraw

    function setActiveTarget(value: number) {
      activeTargetRef.current = value
      lastFrameTimeRef.current = null
      requestDraw()
    }

    function syncActiveTarget() {
      setActiveTarget(pointerInsideRef.current && !focusWithinRef.current ? 1 : 0)
    }

    function draw(timestamp: number) {
      const context = canvasElement.getContext("2d")

      frameRef.current = null

      if (!context) {
        return
      }

      const previousTime = lastFrameTimeRef.current ?? timestamp
      const elapsed = timestamp - previousTime
      const activeTarget = activeTargetRef.current
      const nextActiveValue =
        activeValueRef.current +
        (activeTarget - activeValueRef.current) * Math.min(1, elapsed / revealTransitionMs)

      activeValueRef.current = nextActiveValue

      const rippleTarget = rippleTargetRef.current
      const nextRippleValue =
        rippleValueRef.current +
        (rippleTarget - rippleValueRef.current) * Math.min(1, elapsed / rippleFadeMs)

      rippleValueRef.current = nextRippleValue

      // Advance the outward travel only while the ripple is (fading in/)active,
      // and reset once it has fully faded so the next hover starts at the origin.
      if (nextRippleValue > 0.001) {
        rippleTravelRef.current += (elapsed / 1000) * rippleSpeed
      } else {
        rippleTravelRef.current = 0
      }

      lastFrameTimeRef.current = timestamp

      const bounds = canvasElement.getBoundingClientRect()

      if (bounds.width <= 0 || bounds.height <= 0) {
        if (warmupFramesRef.current > 0) {
          warmupFramesRef.current -= 1
          requestDraw()
        }

        return
      }

      const pixelRatio = window.devicePixelRatio || 1
      const width = Math.max(1, Math.round(bounds.width * pixelRatio))
      const height = Math.max(1, Math.round(bounds.height * pixelRatio))

      // Only resize the backing store; the canvas keeps its CSS `inset-0 /
      // 100%` box so it always stretches to the parent. Pinning style.width/
      // height to pixels would over-constrain the absolute box (fixed height +
      // top/bottom:0) and freeze it at a stale height when the row grows later.
      if (canvasElement.width !== width || canvasElement.height !== height) {
        canvasElement.width = width
        canvasElement.height = height
      }

      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
      context.clearRect(0, 0, bounds.width, bounds.height)

      const random = mulberry32(seed)
      const columns = Math.ceil(bounds.width / gap)
      const rows = Math.ceil(bounds.height / gap)
      const pointer = pointerRef.current
      const pointerX = pointer ? pointer.x - bounds.left : bounds.width / 2
      const pointerY = pointer ? pointer.y - bounds.top : bounds.height / 2
      const color = colorRef.current

      const rippleActiveNow = nextRippleValue > 0.001
      const rippleColor = rippleColorRef.current
      const rippleColorAlt = rippleColorAltRef.current
      const rippleTravel = rippleTravelRef.current
      const originElement = rippleOriginRef.current?.current ?? null
      const originBounds = rippleActiveNow ? originElement?.getBoundingClientRect() : undefined
      const originX = originBounds
        ? originBounds.left + originBounds.width / 2 - bounds.left
        : bounds.width / 2
      const originY = originBounds
        ? originBounds.top + originBounds.height / 2 - bounds.top
        : bounds.height / 2

      for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
          const centerX = column * gap + gap / 2
          const centerY = row * gap + gap / 2
          // Draw the two random values in a fixed order so every cell keeps
          // stable size/opacity across redraws (the reveal animation).
          const sizeRandom = random()
          const opacityRandom = random()
          // Square the size roll so most cells stay small, with a little more
          // variance toward the lower end (no oversized squares).
          const baseSide = dotSize + sizeRandom * sizeRandom * dotSize * sizeRange
          const baseOpacity = 0.075 + (1 - opacityRandom * opacityRange) * 0.15
          const distance = pointer ? Math.hypot(pointerX - centerX, pointerY - centerY) : Infinity
          const proximity = Math.max(0, 1 - distance / revealRadius) * nextActiveValue
          const easedProximity = proximity * proximity * (3 - 2 * proximity)

          // Expanding ring crests: a moving band whose distance to the nearest
          // crest lights up dots, fading with distance from the origin.
          let rippleIntensity = 0
          // Crest tint: successive rings alternate between the two ripple colors.
          let crestR = rippleColor.r
          let crestG = rippleColor.g
          let crestB = rippleColor.b
          if (rippleActiveNow) {
            const rippleDistance = Math.hypot(centerX - originX, centerY - originY)
            const phase = rippleDistance - rippleTravel
            const wrapped = ((phase % ripplePeriod) + ripplePeriod) % ripplePeriod
            const crestDistance = Math.min(wrapped, ripplePeriod - wrapped)
            const band = Math.max(0, 1 - crestDistance / rippleBandWidth)
            const easedBand = band * band * (3 - 2 * band)
            const falloff = Math.max(0, 1 - rippleDistance / rippleMaxRadius)
            rippleIntensity = easedBand * falloff * nextRippleValue

            const nearestRing = Math.round(phase / ripplePeriod)
            const altMix = ((nearestRing % 2) + 2) % 2 === 0 ? 0 : 1
            crestR = rippleColor.r + (rippleColorAlt.r - rippleColor.r) * altMix
            crestG = rippleColor.g + (rippleColorAlt.g - rippleColor.g) * altMix
            crestB = rippleColor.b + (rippleColorAlt.b - rippleColor.b) * altMix
          }

          const side = baseSide * (1 + easedProximity * 0.28 + rippleIntensity * 0.85)
          const opacity = Math.min(
            0.85,
            baseOpacity + easedProximity * 0.17 + rippleIntensity * 0.55,
          )
          const red = Math.round(color.r + (crestR - color.r) * rippleIntensity)
          const green = Math.round(color.g + (crestG - color.g) * rippleIntensity)
          const blue = Math.round(color.b + (crestB - color.b) * rippleIntensity)

          context.fillStyle = `rgba(${red}, ${green}, ${blue}, ${opacity})`
          context.fillRect(centerX - side / 2, centerY - side / 2, side, side)
        }
      }

      context.setTransform(1, 0, 0, 1, 0, 0)

      const rippleAnimating = rippleTarget > 0 || nextRippleValue > 0.001

      if (warmupFramesRef.current > 0) {
        warmupFramesRef.current -= 1
      }

      if (
        Math.abs(activeTarget - nextActiveValue) > 0.001 ||
        rippleAnimating ||
        warmupFramesRef.current > 0
      ) {
        requestDraw()
      }
    }

    function handlePointerEnter(event: PointerEvent) {
      pointerInsideRef.current = true
      pointerRef.current = { x: event.clientX, y: event.clientY }
      syncActiveTarget()
    }

    function handlePointerMove(event: PointerEvent) {
      pointerRef.current = { x: event.clientX, y: event.clientY }
      requestDraw()
    }

    function handlePointerLeave() {
      pointerInsideRef.current = false
      syncActiveTarget()
    }

    function handleFocusIn() {
      focusWithinRef.current = true
      syncActiveTarget()
    }

    function handleFocusOut(event: FocusEvent) {
      if (event.relatedTarget instanceof Node && parentElement.contains(event.relatedTarget)) {
        return
      }

      focusWithinRef.current = false
      syncActiveTarget()
    }

    const resizeObserver = new ResizeObserver(requestDraw)
    const themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class" && mutation.target === document.documentElement) {
          colorRef.current = getDotColor()
          requestDraw()
        }
      }
    })

    parentElement.addEventListener("pointerenter", handlePointerEnter)
    parentElement.addEventListener("pointermove", handlePointerMove, { passive: true })
    parentElement.addEventListener("pointerleave", handlePointerLeave)
    parentElement.addEventListener("focusin", handleFocusIn)
    parentElement.addEventListener("focusout", handleFocusOut)
    resizeObserver.observe(parentElement)
    resizeObserver.observe(canvasElement)
    themeObserver.observe(document.documentElement, { attributes: true })
    requestDraw()

    return () => {
      parentElement.removeEventListener("pointerenter", handlePointerEnter)
      parentElement.removeEventListener("pointermove", handlePointerMove)
      parentElement.removeEventListener("pointerleave", handlePointerLeave)
      parentElement.removeEventListener("focusin", handleFocusIn)
      parentElement.removeEventListener("focusout", handleFocusOut)
      resizeObserver.disconnect()
      themeObserver.disconnect()
      requestDrawRef.current = null

      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [gap, dotSize, sizeRange, opacityRange, revealRadius, seed])

  useEffect(() => {
    rippleTargetRef.current = rippleActive ? 1 : 0
    // Restart frame timing so the first ripple frame measures a small delta
    // instead of the gap since the canvas last idled.
    lastFrameTimeRef.current = null
    requestDrawRef.current?.()
  }, [rippleActive])

  return (
    <canvas
      aria-hidden="true"
      className={className}
      ref={canvasRef}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    />
  )
}
